import React from 'react';

import { DateTime } from 'luxon';


import { getUserInfo, getManagerCompany, getManagerStudents, getManagerCustomReport } from './ajax';

import { formatNumber, shallowCopy } from './helpers';

import IconDownload from './icons/IconDownload';
import IconFilter from './icons/IconFilter';
import IconChevronLeft from './icons/IconChevronLeft';
import IconChevronRight from './icons/IconChevronRight';
import IconChevronDown from './icons/IconChevronDown';
import IconSort from './icons/IconSort';
import IconClose from './icons/IconClose';

import FormCheckbox from './components/FormCheckbox';
import Modal from './components/Modal';
import LoadingCenter from './components/LoadingCenter';
import DateSelector4 from './components/DateSelector4';


class ManagerStudentManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      state: 'loading', // 'loading', 'loaded'

      // filtering & search string
      filters: this.getEmptyFilters(),
      // isContractFilterVisible: false,
      // isCourseFilterVisible: false,
      // isJoinDateFilterVisible: false,
      // isRoleFilterVisible: false,
      areFiltersApplied: false,
      search: '',

      // student ordering
      order: 'name',
      ascending: true,

      // student pagination
      pageCurrent: 1,
      pageSize: 20,

      // server data
      company: null,
      user: null,
      students: [],
      courses: [],

      // students max
      studentsMax: 0,
    };
    this.getEmptyFilters = this.getEmptyFilters.bind(this);
    this.fetch = this.fetch.bind(this);
    this.reorder = this.reorder.bind(this);
    this.onFiltersChanged = this.onFiltersChanged.bind(this);
    this.onSearchChanged = this.onSearchChanged.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.onStudentSelect = this.onStudentSelect.bind(this);
    // this.onDownloadReport = this.onDownloadReport.bind(this);
  }

  getEmptyFilters() {
    return {
      companyPlanIds: [],
      courseIds: [],
      joinedAtStart: null,
      joinedAtEnd: null,
      role: null,
    };
  }

  async componentDidMount() {
    try {
      console.log(`[ManagerStudentManagement] Running componentDidMount()..`);

      // fetch user
      let user = await getUserInfo();
      console.log(`[ManagerStudentManagement] Fetched user.`, user);

      // fetch company
      if (!user.companyId) {
        console.error('Missing companyId.');
        return;
      }
      let company = await getManagerCompany(user.companyId);
      console.log('[ManagerStudentManagement] Fetched company.', company);

      // calculate selectable courses
      let courses = [];
      for (let plan of company.plans) {
        for (let course of plan.courses) {
          if (!courses.find(c => c.id === course.id)) {
            courses.push(course);
          }
        }
      }

      // fetch students
      let students = await this.fetch(this.state.filters, this.state.search);
      console.log(`[ManagerStudentManagement] Fetched and set students?`, students);

      this.setState({ state: 'loaded', user, company, students, studentsMax: students.length, courses });

    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in componentDidMount()`, e);
    }
  }

  async fetch(filters, search) {
    try {
      console.log(`[ManagerStudentManagement] Fetching students...`, { filters, search });
      this.setState({ state: 'loading' });
      let students = await getManagerStudents(filters, search);
      let areFiltersApplied = filters.companyPlanIds.length > 0 || filters.courseIds.length > 0 || filters.joinedAtStart || filters.joinedAtEnd || filters.role !== null;
      this.setState({ state: 'loaded', students, pageCurrent: 1, areFiltersApplied });
      console.log(`[ManagerStudentManagement] Fetched students.`, { students });
      return students;
    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in refetchManagerStudents()`, e);
    }
  }

  async reorder(students, order, ascending) {
    try {
      console.log(`[ManagerStudentManagement] Reordering....`, { order, ascending, students });
      // INPROG do sorting
      students.sort((a, b) => {
        // string comparison
        if (a[order].toLowerCase && b[order].toLowerCase) {
          if (a[order].toLowerCase() > b[order].toLowerCase()) {
            return this.state.ascending ? 1 : -1;
          }
          else return this.state.ascending ? -1 : 1;
        }
        // non-string comparison
        if (a[order] > b[order]) {
          return this.state.ascending ? 1 : -1;
        }
        else return this.state.ascending ? -1 : 1;
      });
      console.log(`[ManagerStudentManagement] Reordered?`, { order, ascending, students });

      this.setState({ order, ascending, students });
    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in reorder()`, e);
    }
  }

  async onFiltersChanged(filters) {
    try {
      console.log(`[ManagerStudentManagement] Filters Changed...`, { filters });

      // set filters, empty search string
      this.setState({ filters, search: '' });

      // if (JSON.stringify(filters) !== JSON.stringify(this.state.filters) || search !== this.state.search) {
      //   this.setState({ state: 'loading' });
      //   let students = await this.fetch(filters, search);
      //   this.setState({ state: 'loaded', students, pageCurrent: 1 });
      // }

    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in onFiltersChanged()`, e);
    }
  }

  async onSearchChanged(search) {
    try {
      console.log(`[ManagerStudentManagement] Search changed...`, { search });
      let filters = this.state.filters;
      if (search === '') {
        // if search is empty, set filters to empty
        filters = this.getEmptyFilters();
      }
      this.setState({ state: 'loading', search, filters });
      let students = await this.fetch(this.state.filters, search);
      this.setState({ state: 'loaded', students, pageCurrent: 1 });
    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in onSearchChanged()`, e);
    }
  }

  async onPageChange(pageCurrent) {
    try {
      console.log(`[ManagerStudentManagement] Page Changed....`, { pageCurrent });
      this.setState({ pageCurrent });
    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in onPageChange()`, e);
    }
  }

  async onStudentSelect(id = null) {
    try {
      console.log(`[ManagerStudentManagement] Student Selected....`, { id });
      if (!id) {
        if(this.state.students.filter(s => s.isSelected).length === this.state.students.length) {
          // all are selected, so deselect all
          for (let student of this.state.students) {
            student.isSelected = false;
          }
        }
        else {
          // not all are selected, so select all
          for (let student of this.state.students) {
            student.isSelected = true;
          }
        }
        this.setState({ students: this.state.students });
        return;
      }
      for (let student of this.state.students) {
        if (student.id === id) {
          console.log(`[ManagerStudentManagement] Selected student.`, student);
          student.isSelected = student.isSelected ? false : true;
        }
      }
      this.setState({ students: this.state.students });
    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in onStudentSelect()`, e);
    }
  }

  render() {

    return (
      <div className='pi3admin'>

        <h1>{this.state.company ? this.state.company.name : ''} Student Management</h1>

        <br />

        <div className='shadedContainer'>

          { this.state.company && this.state.students &&
            <div>

              <div className='pi3row pad'>
                <div className='col-1-of-3'>
                  <div>

                    { !this.state.areFiltersApplied &&
                      <div style={{ display: 'flex' }}>
                        <div style={{ margin: '.5rem .5rem 0 0' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="26" viewBox="0 0 28 26" fill="none">
                            <path opacity="0.587821" d="M13.7636 11.5556C10.3863 11.5556 7.64844 8.96882 7.64844 5.77782C7.64844 2.58682 10.3863 0 13.7636 0C17.141 0 19.8789 2.58682 19.8789 5.77782C19.8789 8.96882 17.141 11.5556 13.7636 11.5556Z" fill="#C62F75"/>
                            <path d="M0.000996297 24.8433C0.593571 17.9493 6.51562 14.4443 13.7338 14.4443C21.0535 14.4443 27.0673 17.7568 27.5153 24.8444C27.5331 25.1268 27.5153 26 26.3668 26C20.7017 26 12.2835 26 1.11221 26C0.728799 26 -0.031282 25.2188 0.000996297 24.8433Z" fill="#C62F75"/>
                          </svg>
                        </div>
                        <div>
                          <h1 style={{ lineHeight: '3.2rem' }}>{formatNumber(this.state.studentsMax)}</h1>
                          <div className='small' style={{ margin: '0', lineHeight: '1.2rem' }}>Enrolled Students</div>
                        </div>
                      </div>
                    }

                    { this.state.areFiltersApplied &&
                      <div style={{ display: 'flex' }}>
                          <div style={{ margin: '.5rem .5rem 0 0' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="26" viewBox="0 0 28 26" fill="none">
                            <path opacity="0.587821" d="M13.7636 11.5556C10.3863 11.5556 7.64844 8.96882 7.64844 5.77782C7.64844 2.58682 10.3863 0 13.7636 0C17.141 0 19.8789 2.58682 19.8789 5.77782C19.8789 8.96882 17.141 11.5556 13.7636 11.5556Z" fill="#2e7cf6"/>
                            <path d="M0.000996297 24.8433C0.593571 17.9493 6.51562 14.4443 13.7338 14.4443C21.0535 14.4443 27.0673 17.7568 27.5153 24.8444C27.5331 25.1268 27.5153 26 26.3668 26C20.7017 26 12.2835 26 1.11221 26C0.728799 26 -0.031282 25.2188 0.000996297 24.8433Z" fill="#2e7cf6"/>
                          </svg>
                        </div>
                        <div>
                          <h1 style={{ lineHeight: '3.2rem', color: '#2e7cf6' }}>{formatNumber(this.state.students.length)}</h1>
                          <div className='small' style={{ margin: '0 0 0 0', lineHeight: '1.2rem' }}>Viewing</div>
                        </div>
                        <h1 style={{ lineHeight: '3.5rem', fontWeight: '400', margin: '0 1rem 0 1rem' }}>/</h1>
                        <div>
                          <h1 style={{ lineHeight: '3.2rem', fontWeight: '400' }}>{formatNumber(this.state.studentsMax)}</h1>
                          <div className='small' style={{ margin: '0 0 0 0', lineHeight: '1.2rem' }}>Enrolled Students</div>
                        </div>
                      </div>
                    }

                  </div>

                </div>
                <div className='col-2-of-3'>
                  <StudentSearch
                    company={this.state.company}
                    courses={this.state.courses}
                    students={this.state.students}
                    filters={this.state.filters}
                    search={this.state.search}
                    onChange={this.onSearchChanged}
                  />

                  <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-end', margin: '1.6rem 0 0 0' }}>
                    <StudentFilters
                      company={this.state.company}
                      courses={this.state.courses}
                      students={this.state.students}
                      filters={this.state.filters}
                      search={this.state.search}
                      onChange={this.onFiltersChanged}
                      onDownloadReport={this.onDownloadReport}
                      fetch={this.fetch}
                      areFiltersApplied={this.state.areFiltersApplied}
                    />
                    <StudentPager
                      pageCurrent={this.state.pageCurrent}
                      pageSize={this.state.pageSize}
                      onPageChange={this.onPageChange}
                      students={this.state.students}
                    />
                  </div>

                </div>
              </div>


            </div>
          }

          { this.state.state === 'loading' &&
            <LoadingCenter />
          }

          { this.state.state === 'loaded' &&
            <div>
              <StudentTable
                students={this.state.students}
                pageCurrent={this.state.pageCurrent}
                pageSize={this.state.pageSize}
                order={this.state.order}
                ascending={this.state.ascending}
                reorder={this.reorder}
                onStudentSelect={this.onStudentSelect}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '.5rem 2rem 1.6rem 0' }}>
                <StudentPager
                  pageCurrent={this.state.pageCurrent}
                  pageSize={this.state.pageSize}
                  onPageChange={this.onPageChange}
                  students={this.state.students}
                />
              </div>
            </div>
          }

        </div>
      </div>
    );
 
  }
}

export default ManagerStudentManagement;



// <StudentSearch filters={} search='' company=[} onChange=() />
class StudentSearch extends React.Component {
  constructor(props) {
    super(props);
  }
  render() {
    return (
      <div className="">
        <input
          type="text"
          name='search'
          className='gray'
          value={this.props.search}
          placeholder='Search...'
          onChange={ev => this.props.onChange(ev.target.value )}
        />
      </div>
    )
  }
}

// <StudentFilters filters={} search='' company=[} onChange=() />
class StudentFilters extends React.Component {
  constructor(props) {
    super(props);
    this.state = {

      // filters modal
      areFiltersVisible: false,

      // report modal
      isDownloadReportVisible: false,
      downloadReportStage: 'download', // 'download', 'downloading', 'downloaded'
    };
    this.onDownloadReport = this.onDownloadReport.bind(this);
  }

  async onDownloadReport() {
    try {
      let selectedUserIds = this.props.students.filter(s => s.isSelected).map(s => s.id);
      if (selectedUserIds.length === 0) {
        // all the userIds are selected, so get all the userIds
        selectedUserIds = this.props.students.map(s => s.id);
      }
      console.log(`[ManagerStudentManagement] Downloading report...`, { selectedUserIds });
      
      let report = await getManagerCustomReport(selectedUserIds);
      console.log(`[ManagerStudentManagement] Downloaded report.`, { report });

      // report excel is a buffer, turn to file and initiate download
      let uint8Array = new Uint8Array(report.excel.data); // convert Buffer to Uint8Array
      let blob = new Blob([uint8Array], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      let url = window.URL.createObjectURL(blob);
      let a = document.createElement('a');
      a.href = url;
      // create date string for file name
      let dateString = DateTime.local().toFormat('yyyy-LL-dd_HH-mm');
      a.download = `Report ${dateString}.xlsx`;
      // a.download = 'Product Institute Report .xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      this.setState({
        downloadReportStage: 'download'
      })

    }
    catch(e) {
      console.error(`[ManagerStudentManagement] Error in onDownloadReport()`, e);
    }
  }

  render() {

    // download report
    let areAnyStudentsSelected = this.props.students.filter(s => s.isSelected).length > 0;
    // let areAnyPlans
    let isDownloadReportDismissable = this.state.downloadReportStage === 'download' ? true : false; // only dismissable when not downloading
    let downloadReportLabel = `All Students (${this.props.students.length})`;
    if (this.props.areFiltersApplied) {
      downloadReportLabel = `Filtered Students (${this.props.students.length})`;
    }
    else if (areAnyStudentsSelected) {
      downloadReportLabel = `Selected Students (${this.props.students.filter(s => s.isSelected).length})`;
    }

    return (
      <div className='studentManagementFilters'>

        <Modal
          isOpen={this.state.isDownloadReportVisible}
          isEasyDismiss={isDownloadReportDismissable} // only dismissable when not downloading
          onClose={() => this.setState({ isDownloadReportVisible: isDownloadReportDismissable ? false : true })}
        >

          <div className='pi3modalHeader'>
            <h3>&nbsp;</h3>
            <a className='actionLink' onClick={() => this.setState({ isDownloadReportVisible: isDownloadReportDismissable ? false : true })}>
              <IconClose />
            </a>
          </div>

          <div className='pi3modalContent'>
            <h3 style={{ textAlign: 'center' }}>Download Report for <mark>{downloadReportLabel}</mark></h3>
            <br />
          </div>

          <div className='pi3modalFooterBorder'>

              {this.state.downloadReportStage === 'download' &&
                <div className='pi3row'>
                  <button className='buttonSecondary' onClick={() => {
                      this.setState({ isDownloadReportVisible: false });
                    }}>
                    Cancel
                  </button>
                  <button className='buttonPrimary' onClick={() => {
                      this.setState({ downloadReportStage: 'downloading' });
                      this.onDownloadReport();
                    }}>
                    Download
                  </button>
                </div>
              }

              {this.state.downloadReportStage === 'downloading' &&
                <div className='pi3row'>
                  <button className='buttonSecondary' disabled>
                    Cancel
                  </button>
                  <button className='buttonPrimary' disabled>
                    Downloading...
                  </button>
                </div>
              }

          </div>


        </Modal>

        <Modal
          isOpen={this.state.areFiltersVisible}
          isEasyDismiss={true}
          onClose={() => this.setState({ areFiltersVisible: false })}
        >

          <div className='pi3modalHeaderBorder'> 
            <h3><IconFilter /> Filter Students</h3>
            <a className='actionLink' onClick={() => this.setState({ areFiltersVisible: false })}>
              <IconClose />
            </a>
          </div>

          <div className='pi3modalContent'>

            <FilterContracts
              company={this.props.company}
              filters={this.props.filters}
              onChange={this.props.onChange}
            />

            <FilterEnrollments
              company={this.props.company}
              courses={this.props.courses}
              filters={this.props.filters}
              onChange={this.props.onChange}
            />

            <FilterJoinDates
              filters={this.props.filters}
              onChange={this.props.onChange}
            />

            <FilterRole
              filters={this.props.filters}
              onChange={this.props.onChange}
            />

          </div>

          <div className='pi3modalFooterBorder'>
            <div className='pi3row'>
              <button className='buttonSecondary' onClick={() => {
                let filtersClean = { companyPlanIds: [], courseIds: [], joinedAtStart: null, joinedAtEnd: null, role: null, areFiltersApplied: false }
                // reset filters to default
                this.props.onChange(filtersClean);
                // refetch students with new filters
                this.props.fetch( filtersClean, this.props.search);
                // hide filters modal
                this.setState({ areFiltersVisible: false });
              }}>
                Clear
              </button>
              <button className='buttonPrimary' onClick={() => {
                // refetch students with new filters
                this.props.fetch(this.props.filters, this.props.search);
                // hide filters modal
                this.setState({ areFiltersVisible: false });
              }}>
                Apply Filters
              </button>
            </div>
              
          </div>

        </Modal>

        <button className={this.props.areFiltersApplied ? 'buttonSecondary small active' : 'buttonSecondary small'} onClick={() => this.setState({ areFiltersVisible: !this.state.areFiltersVisible })} >
          <IconFilter />
          Filter
        </button>

        <button className='buttonSecondary small' onClick={() => this.setState({ isDownloadReportVisible: true })}>
          <IconDownload />
          Download Report
        </button>

      </div>
    );
  }
}

// <FilterContracts company={this.props.company} filters={this.props.filters} onChange={this.props.onChange} />
class FilterContracts extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMinimized: true,
    };
    this.onChange = this.onChange.bind(this);
  }
  onChange(companyPlanId) {
    let filters = JSON.parse(JSON.stringify(this.props.filters));
    if (filters.companyPlanIds.includes(companyPlanId)) {
      filters.companyPlanIds = filters.companyPlanIds.filter(cpid => cpid !== companyPlanId);
      this.props.onChange(filters);
      return;
    }
    else {
      filters.companyPlanIds.push(companyPlanId);
      this.props.onChange(filters);
      return;
    }
  }
  render() {
    let plans = this.props.company.plans;
    let selectedPlans  = this.props.filters.companyPlanIds;

    let items = plans.map((plan) => (
      <div key={plan.id}>
        <FormCheckbox
          isChecked={selectedPlans.includes(plan.id)}
          isDisabled={false}
          label={plan.name}
          onChange={() => this.onChange(plan.id)}
        />
      </div>
    ));

    return (
      <div className='studentManagementFilterContainer'>

        <div className='studentManagementFilter'>
          <a onClick={() => this.setState({ isMinimized: !this.state.isMinimized })}>
            <h4>Contract</h4>
          </a>
          { this.state.isMinimized &&
            <a className='actionLink' onClick={() => this.setState({ isMinimized: false })}>
              <IconChevronDown />
            </a>
          }
          { !this.state.isMinimized &&
            <a className='actionLink' onClick={() => {
              this.setState({ isMinimized: true });
              this.props.onChange({ ...this.props.filters, companyPlanIds: [] } );
            }}>
              Clear
            </a>
          }
        </div>

        { !this.state.isMinimized &&
          <div className='studentManagementFilterItems'>
            {items}
          </div>
        }

      </div>
    );
  }
}

class FilterEnrollments extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMinimized: true,
    };
    this.onChange = this.onChange.bind(this);
  }
  onChange(courseId) {
    let filters = JSON.parse(JSON.stringify(this.props.filters));
    if (filters.courseIds.includes(courseId)) {
      filters.courseIds = filters.courseIds.filter(cid => cid !== courseId);
      this.props.onChange(filters);
      return;
    }
    else {
      filters.courseIds.push(courseId);
      this.props.onChange(filters);
      return;
    }
  }
  render() {
    let courses = this.props.courses;
    let selectedCourses = this.props.filters.courseIds;

    let items = courses.map((course) => (
      <div key={course.id}>
        <FormCheckbox
          isChecked={selectedCourses.includes(course.id)}
          isDisabled={false}
          label={course.name}
          onChange={() => this.onChange(course.id)}
        />
       </div>
     ));

    return (
      <div className='studentManagementFilterContainer'>

        <div className='studentManagementFilter'>
          <a onClick={() => this.setState({ isMinimized: !this.state.isMinimized })}>
            <h4>Course Enrollments</h4>
          </a>
          { this.state.isMinimized &&
            <a className='actionLink' onClick={() => this.setState({ isMinimized: false })}>
              <IconChevronDown />
            </a>
          }
          { !this.state.isMinimized &&
            <a className='actionLink' onClick={() => {
              this.setState({ isMinimized: true });
              this.props.onChange({ ...this.props.filters, courseIds: [] } );
            }}>
              Clear
            </a>
          }
        </div>

        { !this.state.isMinimized &&
          <div className='studentManagementFilterItems'>
            {items}
          </div>
        }
      </div>
    );
  }
}

// <FilterJoinDates filters={} onChange=() />
class FilterJoinDates extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMinimized: true,
    };
    // this.onChange = this.onChange.bind(this);
  }
  // onChange(field, value) {
  //   let filters = JSON.parse(JSON.stringify(this.props.filters)); // TODO change this
  //   filters[field] = value;
  //   this.props.onChange(filters);
  // }
  render() {
    return (
      <div className='studentManagementFilterContainer pi3form'>

        <div className='studentManagementFilter'>
          <a onClick={() => this.setState({ isMinimized: !this.state.isMinimized })}>
            <h4>Join Date Range</h4>
          </a>
          { this.state.isMinimized &&
            <a className='actionLink' onClick={() => this.setState({ isMinimized: false })}>
              <IconChevronDown />
            </a>
          }
          { !this.state.isMinimized &&
            <a className='actionLink' onClick={() => {
              this.setState({ isMinimized: true });
              this.props.onChange({ ...this.props.filters, joinedAtStart: null, joinedAtEnd: null });
            }}>
              Clear
            </a>
          }
        </div>

        { !this.state.isMinimized &&
          <div className='studentManagementFilterItems' style={{ display: 'flex', gap: '1rem' }}>
            <div>
              Start
              <DateSelector4
                value={this.props.filters.joinedAtStart}
                onChange={joinedAtStart => this.props.onChange({ ...this.props.filters, joinedAtStart })}
              />
            </div>
            <div>
              End
              <DateSelector4
                value={this.props.filters.joinedAtEnd}
                onChange={joinedAtEnd => this.props.onChange({ ...this.props.filters, joinedAtEnd })}
              />
            </div>
          </div>
        }

      </div>
    );
  }
}

// <FilterRole filters={} onChange=() />
class FilterRole extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMinimized: true,
    };
    this.onChange = this.onChange.bind(this);
  }
  onChange(role) {
    let filters = JSON.parse(JSON.stringify(this.props.filters));
    filters.role = role === 'null' ? null : parseInt(role);
    this.props.onChange(filters);
  }
  render() {
    return (
      <div className='studentManagementFilterContainer'>

        <div className='studentManagementFilter'>
          <a onClick={() => this.setState({ isMinimized: !this.state.isMinimized })}>
            <h4>Role</h4>
          </a>
          { this.state.isMinimized &&
            <a className='actionLink' onClick={() => this.setState({ isMinimized: false })}>
              <IconChevronDown />
            </a>
          }
          { !this.state.isMinimized &&
            <a className='actionLink' onClick={() => {
              this.setState({ isMinimized: true });
              this.props.onChange({ ...this.props.filters, role: null });
            }}>
              Clear
            </a>
          }
        </div>

        { !this.state.isMinimized &&
          <div className='studentManagementFilterItems'>
            <label>
              <input
                type="radio"
                value='null'
                checked={this.props.filters.role === null}
                onChange={() => this.onChange('null')}
              />
              &nbsp;
              Student
            </label>
            <br />
            <label>
              <input
                type="radio"
                value='1'
                checked={this.props.filters.role === 1}
                onChange={() => this.onChange('1')}
              />
              &nbsp;
              Manager
            </label>
          </div>
        }

      </div>
    );
  }
}

// <StudentPager pageCurrent={} pageSize={} onPageChange=() />
class StudentPager extends React.Component {
  constructor(props) {
    super(props);
  }
  render() {

    let pageCurrent = this.props.pageCurrent;
    let pageLast = Math.ceil(this.props.students.length / this.props.pageSize);

    return (
      <div style={{ display: 'flex', height: '100%', alignItems: 'center', gap: '.5rem', color: '#979797' }}>

        <span className='small'>{pageCurrent} of {pageLast}</span>

        { pageCurrent > 1 &&
          <button className='buttonPager' onClick={() => this.props.onPageChange(this.props.pageCurrent - 1)}>
            <IconChevronLeft />
          </button>
        }
        { pageCurrent === 1 &&
          <button className='buttonPager' disabled>
            <IconChevronLeft />
          </button>
        }

        { pageCurrent < pageLast &&
          <button className='buttonPager' onClick={() => this.props.onPageChange(this.props.pageCurrent + 1)}>
            <IconChevronRight />
          </button>
        }
        { pageCurrent === pageLast &&
          <button className='buttonPager' disabled>
            <IconChevronRight />
          </button>
        }
      </div>
    );
  }
}

// <StudentTable students=[] reorder=() />
class StudentTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  render() {

    console.log(`[StudentTable] Rendering...`, { order: this.state.order, ascending: this.state.ascending });

    let students = this.props.students;
    let studentsPage = this.props.students.slice((this.props.pageCurrent - 1) * this.props.pageSize, this.props.pageCurrent * this.props.pageSize);

    let studentsRender = [];
    for (let student of studentsPage) {

      let joinedAtString = DateTime.fromISO(student.joinedAt).toFormat('M/d/yy');

      studentsRender.push(
        <tr key={student.id}>
          <td>
            {/* <input
              type='checkbox'
              className='gray'
              checked={student.isSelected ? true : false}
              onChange={() => this.props.onStudentSelect(student.id)}
            /> */}
            <div style={{ margin: '.5rem 0 0 0'}}>
              <FormCheckbox
                isChecked={student.isSelected ? true : false}
                isDisabled={false}
                label=''
                onChange={() => this.props.onStudentSelect(student.id)}
              />
            </div>
          </td>
          <td>{student.name}</td>
          <td><span className='accent'>{student.email}</span></td>
          <td>{joinedAtString}</td>
        </tr>
      );
    }

    let numSelected = students.filter(s => s.isSelected).length;

    return (

      <table className='adminTable'>
        <thead>
          <tr>

            <th style={{ width: '4rem' }}>
              <div style={{ margin: '.5rem 0 0 0'}}>
                <FormCheckbox
                  isChecked={this.props.students.filter(s => s.isSelected).length === this.props.students.length}
                  isDisabled={false}
                  label=''
                  onChange={() => this.props.onStudentSelect()}
                />
              </div>
            </th>

            <th style={{ width: '30%' }}>
              {
                numSelected > 0 &&
                <strong className='accent'>{this.props.students.filter(s => s.isSelected).length}&nbsp;&nbsp;</strong>
              }
              <span
                onClick={() => {
                  this.props.reorder(students, 'name', !this.props.ascending);
                }}
              >
                Name
                &nbsp;
                <IconSort className='adminTableIcon' />
              </span>
            </th>

            <th style={{ width: '50%' }}
              onClick={() => {
                this.props.reorder(students, 'email', !this.props.ascending);
              }}
            >
              Email
              &nbsp;
              <IconSort className='adminTableIcon' />
            </th>

            <th
              onClick={() => {
                this.props.reorder(students, 'joinedAt', !this.props.ascending);
              }}
            >
              Joined at
              &nbsp;
              <IconSort className='adminTableIcon' />
            </th>

          </tr>
        </thead>
        <tbody>
          {studentsRender}
        </tbody>
      </table>
    )
  }
}
